// 生成器后端有多地部署（内地 cn-hangzhou / 香港 cn-hongkong），浏览器不知道
// 哪个更快，所以首次访问时并发探测两个端点，谁先应答用谁，结果缓存 7 天；
// 之后如果请求失败（网络层错误），自动切到另一个端点重试一次。
//
// 探测用 mode:'no-cors'：不透明响应也算「可达」，只有 DNS/TCP/TLS 层失败才会
// reject —— 这样不必关心对方的 CORS 头，探测也不会产生业务副作用（GET 不会
// 命中接口限流）。
//
// 注意：签名只覆盖 method/path/timestamp/body，不含域名，所以换端点不用重签。

import config from './config';
import { ApiAuth } from './auth';

const CACHE_KEY = 'soda-api-endpoint';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const PROBE_TIMEOUT_MS = 3000;

function readCache() {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (c && Date.now() - c.at < CACHE_TTL_MS) return c.url;
  } catch (e) {
    /* 隐私模式等，忽略 */
  }
  return null;
}

function writeCache(url) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ url, at: Date.now() }));
  } catch (e) {
    /* 忽略 */
  }
}

function candidates() {
  const list = config.api.baseURLCandidates || [];
  return list.filter(Boolean);
}

// 探测请求打哪个 URL：base 是 API 前缀（如 .../api/v1/legacy），它本身**不是
// 路由** —— 直接打它每次都会拿到 404，在函数计算控制台留下一条「状态码异常
// 404」。改打同域的 /health（免鉴权、恒 200），探测语义不变。
function probeUrl(base) {
  try {
    const u = new URL(base);
    return u.origin + '/health';
  } catch (e) {
    return base;
  }
}

async function probe(base) {
  const started = performance.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(probeUrl(base), { method: 'GET', mode: 'no-cors', signal: controller.signal, cache: 'no-store' });
    return { url: base, ms: performance.now() - started };
  } catch (e) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// 选出当前该用的端点，并写回 config.api.baseURL（其它调用点直接读它）
export async function ensureApiBase() {
  const list = candidates();
  const fallback = config.api.baseURL;
  if (!list.length) return fallback;

  const cached = readCache();
  if (cached && list.includes(cached)) {
    config.api.baseURL = cached;
    return cached;
  }

  const results = (await Promise.all(list.map(probe))).filter(Boolean);
  results.sort((a, b) => a.ms - b.ms);
  const winner = (results[0] && results[0].url) || fallback;
  config.api.baseURL = winner;
  writeCache(winner);
  return winner;
}

// 当前端点失败时换另一个
function otherBase(current) {
  const list = candidates().filter((u) => u !== current);
  if (!list.length) return null;
  writeCache(list[0]);
  config.api.baseURL = list[0];
  return list[0];
}

// 发请求：网络层失败时自动换端点重试一次（HTTP 4xx/5xx 不重试，那是业务错误）
export async function apiFetch(path, { method = 'POST', body = '', headers = {} } = {}) {
  let base = await ensureApiBase();
  const send = async (b) => {
    const fullPath = `${b}${path}`;
    const authHeaders = ApiAuth.getAuthHeaders(method, fullPath, body);
    return fetch(fullPath, {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders, ...headers },
      body,
    });
  };

  try {
    return await send(base);
  } catch (err) {
    const next = otherBase(base);
    if (!next) throw err;
    return await send(next);
  }
}
